import {
  Box,
  Button,
  FormControlLabel,
  LinearProgress,
  Link as MuiLink,
  Switch,
  Typography,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  GridFooterContainer,
  GridPagination,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import { observer } from "mobx-react";
import { FC, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CustomDataGrid } from "src/Shared/Components";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import { IProspectList } from "../../Interfaces/IProspectList";
import {
  defaultProspectOrder,
  isMyProspect,
  rowCountText,
  sortRowsByField,
} from "../../Tools/GridSortUtils";
import ClientSearch from "../ClientSearch/ClientSearch";
import { PROSPECT_SORT_TYPES, prospectColumns } from "./ProspectsGrid.column";

const FLASH_DURATION_MS = 2500;
// Home opens on 25 rows a page; the two grids page alike.
const DEFAULT_PAGE_SIZE = 25;

const ProspectsGrid: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    rootStore: {
      authStore: { user },
      companyStore: {
        prospects,
        prospectsLoading,
        getProspects,
        prospectsOnlyMine,
        setProspectsOnlyMine,
        loadProspectsOnlyMine,
      },
    },
  } = useStore();

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  // The prospect list arrives whole (one GET; Only my clients and the column sort are applied
  // here), so the grid pages it client-side. Rendering every prospect at once froze the tab as
  // the list grew past a thousand rows. The footer is Home's: a row count beside the page controls.
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [flashRowId, setFlashRowId] = useState<string | null>(null);

  useEffect(() => {
    getProspects();
  }, []);

  const myEmail = user?.emailAddress;

  // The stored preference is keyed on the signed-in user, so it can only be read once that user has
  // resolved. Reading on mount alone used the "anonymous" key on a cold load and always fell back to
  // the default, while the toggle wrote under the email key — the setting looked unpersisted
  // (bug 5702). Keyed on the identity, the read and the write always agree.
  useEffect(() => {
    if (myEmail) loadProspectsOnlyMine();
  }, [myEmail, loadProspectsOnlyMine]);
  const myCount = useMemo(
    () => prospects.filter((x) => isMyProspect(x, myEmail)).length,
    [prospects, myEmail],
  );

  const visibleRows = useMemo(() => {
    const filtered = prospectsOnlyMine
      ? prospects.filter((x) => isMyProspect(x, myEmail))
      : prospects;

    const sort = sortModel[0];
    if (sort && sort.sort) {
      // A clicked header sorts on the real value, blanks last (P.8).
      return sortRowsByField(
        filtered,
        sort.field as keyof IProspectList,
        PROSPECT_SORT_TYPES[sort.field] || "string",
        sort.sort,
      );
    }
    // No column sort: the P.3 ordering stands — my clients first, progress descending.
    return defaultProspectOrder(filtered, myEmail);
  }, [prospects, prospectsOnlyMine, myEmail, sortModel]);

  const hiddenCount = prospects.length - visibleRows.length;

  const backToFirstPage = () =>
    setPagination((current) => ({ ...current, page: 0 }));

  // Narrowing or re-ordering the list from a later page restarts at the first page, as Home does.
  const changeOnlyMine = (value: boolean) => {
    setProspectsOnlyMine(value);
    backToFirstPage();
  };

  const onSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    backToFirstPage();
  };

  // A focus query parameter scrolls the row to centre and flashes it (P.8);
  // when Only my clients would hide the target, the filter clears itself (P.4).
  const focusRowId = searchParams.get("focus");
  useEffect(() => {
    if (!focusRowId || prospectsLoading || prospects.length === 0) return;
    const target = prospects.find((x) => x.companyRowID === focusRowId);
    if (!target) return;

    if (prospectsOnlyMine && !isMyProspect(target, myEmail)) {
      setProspectsOnlyMine(false);
      return; // re-runs once the filter has cleared and the row is visible
    }

    // The row may sit on a later page: turn to that page before scrolling to it.
    const index = visibleRows.findIndex((x) => x.companyRowID === focusRowId);
    if (index < 0) return;
    setPagination((current) => ({
      ...current,
      page: Math.floor(index / current.pageSize),
    }));

    setFlashRowId(focusRowId);
    setSearchParams({}, { replace: true });
    setTimeout(() => {
      document
        .querySelector(`[data-id="${focusRowId}"]`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 100);
    setTimeout(() => setFlashRowId(null), FLASH_DURATION_MS);
  }, [focusRowId, prospects, prospectsLoading, prospectsOnlyMine, visibleRows]);

  const onAction = (row: IProspectList) => {
    // Opens the wizard at the lowest-numbered incomplete step (7 when all complete),
    // without also opening the client record (P.2).
    navigate(`/company/${row.companyRowID}/onboarding?step=${row.onboardingNextStep}`);
  };

  const onRowClick = (params: GridRowParams) => {
    navigate(`/company/${params.id}/overview`);
  };

  const columns = useMemo(() => prospectColumns(onAction), []);

  const Footer = () => (
    <GridFooterContainer>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 2 }}>
        <Typography variant="body2">{rowCountText(visibleRows.length)}</Typography>
        {prospectsOnlyMine && hiddenCount > 0 && (
          <Typography variant="body2" color="text.secondary">
            · {rowCountText(hiddenCount)} hidden by Only my clients —{" "}
            <MuiLink
              component="button"
              type="button"
              onClick={() => changeOnlyMine(false)}
            >
              Show all
            </MuiLink>
          </Typography>
        )}
      </Box>
      <GridPagination />
    </GridFooterContainer>
  );

  const NoRows = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        gap: 1,
      }}
    >
      {prospects.length === 0 ? (
        <>
          <Typography variant="body1">No prospects yet.</Typography>
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            component={Link}
            to="/company/add"
          >
            Add New Client
          </Button>
        </>
      ) : (
        <>
          <Typography variant="body1">
            No prospects are assigned to you.
          </Typography>
          <MuiLink
            component="button"
            type="button"
            onClick={() => changeOnlyMine(false)}
          >
            Show everyone's prospects
          </MuiLink>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ m: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h5">Prospects</Typography>
          <Typography variant="body2" color="text.secondary">
            Clients at Prospect and Onboarding status
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ClientSearch currentTab="prospects" />
          <FormControlLabel
            control={
              <Switch
                checked={prospectsOnlyMine}
                onChange={(e) => changeOnlyMine(e.target.checked)}
              />
            }
            label={`Only my clients (${myCount})`}
          />
          <Button
            variant="contained"
            startIcon={<AddCircleIcon />}
            component={Link}
            to="/company/add"
          >
            Add New Client
          </Button>
        </Box>
      </Box>

      <CustomDataGrid
        sx={{
          "& .row-focus-flash": {
            animation: `rowFocusFlash ${FLASH_DURATION_MS}ms ease-out`,
          },
          "@keyframes rowFocusFlash": {
            "0%": { backgroundColor: "#ffe0b2" },
            "70%": { backgroundColor: "#ffe0b2" },
            "100%": { backgroundColor: "transparent" },
          },
          "& .prospect-row-mine": { backgroundColor: "#fff8f0" },
          "& .MuiDataGrid-row": { cursor: "pointer" },
        }}
        loading={prospectsLoading}
        columns={columns}
        rows={visibleRows}
        getRowId={(row: IProspectList) => row.companyRowID}
        style={{ height: "calc(100vh - 160px)" }}
        paginationMode="client"
        paginationModel={pagination}
        onPaginationModelChange={(model) => setPagination(model)}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        onRowClick={onRowClick}
        // A page of 25 rows is cheap to render whole, and the focus scroll above finds its row in
        // the DOM, which virtualisation would not guarantee.
        disableVirtualization
        getRowClassName={(params) => {
          const classes: string[] = [];
          if (params.id === flashRowId) classes.push("row-focus-flash");
          if (
            !prospectsOnlyMine &&
            isMyProspect(params.row as IProspectList, myEmail)
          )
            classes.push("prospect-row-mine");
          return classes.join(" ");
        }}
        slots={{
          footer: Footer,
          noRowsOverlay: NoRows,
          loadingOverlay: LinearProgress,
        }}
      />
    </Box>
  );
};

export default observer(ProspectsGrid);
